(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const ease = 'cubic-bezier(.22,1,.36,1)';
  const motions = new Set();
  const play = (element, frames, options) => {
    if (!element || reduced.matches || !element.animate) return;
    motions.forEach((active) => { if (active.effect?.target === element) active.cancel(); });
    const motion = element.animate(frames, { easing: ease, ...options });
    motions.add(motion);
    motion.finished.then(() => motions.delete(motion)).catch(() => motions.delete(motion));
  };
  reduced.addEventListener('change', () => {
    if (reduced.matches) motions.forEach((motion) => motion.cancel());
  });
  if (!customElements.get('hydros-world-tabs')) {
    customElements.define('hydros-world-tabs', class extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        const list = this.querySelector('[data-world-tabs]');
        if (!list) return;
        this.tabs = [...list.querySelectorAll('button')];
        this.panels = [...this.querySelectorAll('[data-world-panel]')];
        if (!this.tabs.length || this.tabs.length !== this.panels.length) return;
        this.ready = true;
        list.hidden = false;
        list.setAttribute('role', 'tablist');
        list.setAttribute('aria-label', 'Comprendre les technologies HYDROS');
        this.tabs.forEach((tab, index) => {
          tab.setAttribute('role', 'tab');
          this.panels[index].setAttribute('role', 'tabpanel');
          this.panels[index].setAttribute('aria-labelledby', tab.id);
          tab.addEventListener('click', (event) => this.select(index, event.detail > 0));
          tab.addEventListener('keydown', (event) => {
            const destinations = { ArrowRight: (index + 1) % this.tabs.length, ArrowLeft: (index + this.tabs.length - 1) % this.tabs.length, Home: 0, End: this.tabs.length - 1 };
            if (!(event.key in destinations)) return;
            event.preventDefault();
            this.select(destinations[event.key], false);
            this.tabs[destinations[event.key]].focus();
          });
        });
        this.select(0, false);
      }
      select(index, animate) {
        if (index === this.active) return;
        this.active = index;
        this.tabs.forEach((tab, i) => {
          tab.setAttribute('aria-selected', String(i === index));
          tab.tabIndex = i === index ? 0 : -1;
          this.panels[i].hidden = i !== index;
        });
        if (animate) {
          const panel = this.panels[index];
          play(panel.querySelector('.hw-tech-image img'), [{ opacity: .45, transform: `translateX(${index ? 18 : -18}px)` }, { opacity: 1, transform: 'translateX(0)' }], { duration: 260 });
          play(panel.querySelector('.hw-tech-copy'), [{ opacity: .55, transform: 'translateY(12px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 220 });
        }
      }
    });
  }
  const sections = new WeakMap();
  const initSection = (root) => {
    if (sections.has(root)) return;
    const state = { observer: null, cleanups: [] };
    sections.set(root, state);
    root.querySelectorAll('[data-world-card]').forEach((card) => {
      const select = card.querySelector('[data-ecom-card-variant]');
      const image = card.querySelector('[data-world-product-image]');
      if (!select || !image) return;
      const syncVariant = () => {
        const option = select.selectedOptions[0];
        if (!option || !option.dataset.worldSrc) return;
        image.srcset = option.dataset.worldSrcset || '';
        image.src = option.dataset.worldSrc;
        image.alt = option.dataset.worldAlt || '';
        card.querySelectorAll('[data-world-product-link]').forEach((link) => {
          const url = new URL(link.href, window.location.href);
          url.searchParams.set('variant', option.value);
          link.href = url.toString();
        });
      };
      select.addEventListener('change', syncVariant);
      state.cleanups.push(() => select.removeEventListener('change', syncVariant));
      syncVariant();
    });
    root.querySelectorAll('.hw-hero__content').forEach((content) => {
      play(content.querySelector('h1'), [{ opacity: .5, transform: 'translateY(22px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 680 });
      play(content.querySelector('p'), [{ opacity: .65, transform: 'translateY(12px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 620, delay: 60 });
    });
    root.querySelectorAll('.hw-video-grid video').forEach((video) => {
      const pauseOtherVideos = () => document.querySelectorAll('.hw-video-grid video').forEach((other) => { if (other !== video) other.pause(); });
      video.addEventListener('play', pauseOtherVideos);
      state.cleanups.push(() => video.removeEventListener('play', pauseOtherVideos));
    });
    if (!('IntersectionObserver' in window)) return;
    state.observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      if (entry.target.matches?.('.hw-life__pictures')) {
        entry.target.querySelectorAll('figure').forEach((figure, index) => play(figure, [{ opacity: .55, transform: 'translateY(28px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 600, delay: index * 80 }));
      } else {
        const isProduct = entry.target.matches?.('.ecom-product-card__media');
        play(entry.target, [{ opacity: .4, transform: `translateY(${isProduct ? 32 : 24}px)` }, { opacity: 1, transform: 'translateY(0)' }], { duration: 550 });
      }
      state.observer.unobserve(entry.target);
    }), { threshold: .18 });
    root.querySelectorAll('.hw-shop .ecom-product-card__media,.hw-life__pictures,.hw-section-heading h2,.hw-tech-image,.hw-proof__copy').forEach((element) => state.observer.observe(element));
  };
  const init = (root = document) => {
    const homes = [...root.querySelectorAll('.hw-home')];
    if (root.matches?.('.hw-home')) homes.unshift(root);
    const wrappers = new Set(homes.map((home) => home.closest('.shopify-section') || home));
    wrappers.forEach(initSection);
  };
  const dispose = (root) => {
    const state = sections.get(root);
    if (!state) return;
    state.observer?.disconnect();
    state.cleanups.forEach((cleanup) => cleanup());
    motions.forEach((motion) => {
      if (motion.effect?.target && root.contains(motion.effect.target)) motion.cancel();
    });
    sections.delete(root);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => dispose(event.target));
})();
